<div class="user-detail-container">
  <div class="page-header">
    <div>
      <a routerLink="/access-management/accounts-users" class="back-link">
        <i class="pi pi-arrow-left"></i>
        Back to users
      </a>
      <h1>User Detail</h1>
    </div>
    <div class="header-actions">
      <p-button
        label="Refresh"
        icon="pi pi-refresh"
        severity="primary"
        [text]="true"
        (onClick)="refreshAll()"
      ></p-button>
      <p-button
        label="Delete user"
        icon="pi pi-trash"
        severity="danger"
        [outlined]="true"
        (onClick)="openDeleteDialog()"
      ></p-button>
    </div>
  </div>

  <p-card header="Profile">
    @if (profileResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '40px', height: '40px' }"></p-progressSpinner></div>
    } @else if (profileResource.error()) {
      <p-message severity="error" text="Failed to load profile data from UsersEngine."></p-message>
    } @else if (profileResource.value(); as profile) {
      <div class="detail-grid">
        <div class="detail-row">
          <span class="detail-label">Full Name</span>
          <span class="detail-value">{{ fullName(profile) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Preferred Name</span>
          <span class="detail-value">{{ profile.preferredName || '—' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Email</span>
          <span class="detail-value">{{ profile.email }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Phone</span>
          <span class="detail-value">{{ profile.phone || '—' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Referral Source</span>
          <span class="detail-value">{{ profile.referralSource || '—' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Date of Birth</span>
          <span class="detail-value">{{ profile.dateOfBirth || '—' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Profile Image</span>
          <span class="detail-value">
            @if (profile.imageUrl) {
              <a target="_blank" rel="noopener" [href]="profile.imageUrl">{{ profile.imageUrl }}</a>
            } @else {
              <span class="muted">—</span>
            }
          </span>
        </div>
      </div>
    } @else {
      <p-message severity="warn" text="No profile data found in UsersEngine for this user."></p-message>
    }
  </p-card>

  <p-card header="AME Identity">
    @if (userResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (userResource.error()) {
      <p-message severity="error" text="Failed to load AME user record."></p-message>
    } @else if (userResource.value(); as user) {
      <div class="detail-grid">
        <div class="detail-row">
          <span class="detail-label">User ID</span>
          <span class="detail-value monospace">{{ user.userId }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">External ID</span>
          <span class="detail-value monospace">{{ user.externalId }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Email (AME)</span>
          <span class="detail-value">{{ user.email }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Display Name</span>
          <span class="detail-value">{{ user.name || '—' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="detail-value">
            <p-tag
              [value]="user.isActive ? 'Active' : 'Inactive'"
              [severity]="user.isActive ? 'success' : 'secondary'"
            ></p-tag>
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Created</span>
          <span class="detail-value">{{ formatDate(user.createdOn) }}</span>
        </div>
      </div>
    } @else {
      <p-message severity="warn" text="No AME user record found."></p-message>
    }
  </p-card>

  <p-card header="Groups &amp; Roles">
    <p class="card-subtitle">
      Every group this user belongs to, with their assigned role. Household groups link to the group detail.
    </p>
    @if (groupsResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (groupsResource.error()) {
      <p-message severity="error" text="Failed to load the user's groups."></p-message>
    } @else if (groupRows().length === 0) {
      <p class="muted">This user is not a member of any groups.</p>
    } @else {
      <p-table [value]="groupRows()" [tableStyle]="{ 'min-width': '40rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Group</th>
            <th>Type</th>
            <th>Role</th>
            <th>Household Relationship</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>
              @if (row.isHousehold) {
                <a [routerLink]="['/access-management/households', row.group.groupId]">
                  {{ row.group.name }}
                </a>
              } @else {
                {{ row.group.name }}
              }
            </td>
            <td>
              <p-tag
                [value]="row.isHousehold ? 'Household' : 'Single'"
                [severity]="row.isHousehold ? 'info' : 'secondary'"
              ></p-tag>
            </td>
            <td>{{ row.roleName || '—' }}</td>
            <td>
              @if (row.householdRelationship) {
                <p-tag
                  [value]="row.householdRelationship"
                  [severity]="getHouseholdRelationshipSeverity(row.householdRelationship)"
                ></p-tag>
              } @else {
                <span class="muted">—</span>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  </p-card>

  <p-card header="Permissions">
    <p class="card-subtitle">
      Scopes the user holds through their roles, plus the object-level overrides written about them and by them. An
      override either adds a scope or takes one away, so read the two together: a deny subtracts from the bundle above
      it, including a scope no revocation could reach.
    </p>

    <h3 class="section-heading">Scopes from roles</h3>
    @if (roleScopesResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (roleScopesResource.error()) {
      <p-message severity="error" text="Failed to load the scopes for the user's roles."></p-message>
    } @else if (roleScopeRows().length === 0) {
      <p class="muted">This user has no roles that carry scopes.</p>
    } @else {
      <!--
        One row per membership, naming the household as well as the role: the seeded role ids are
        shared, so the same bundle appears once per household the user holds it in and a deny written
        over one of them is about that one alone.
      -->
      @for (row of roleScopeRows(); track row.key) {
        <div class="role-scopes-row">
          <span class="role-scopes-label">{{ row.roleName }}</span>
          <span class="role-scopes-group muted">{{ row.groupName }}</span>
          @if (row.scopes.length === 0) {
            <span class="muted">No scopes</span>
          } @else {
            <!--
              A bundle tag an in-force deny removes is struck through and says which deny, because
              this row is the input to the decision and not the decision: an operator asking "can
              this member edit the household plan?" reads Plan.* here and stops, while the deny two
              sections down is what the gate applies. A deny that narrows a wildcard without emptying
              it is marked differently, because the leaves that survive are still held and striking
              the tag would claim they are not.
            -->
            <div class="scope-tags">
              @for (roleScope of row.scopes; track roleScope.roleScopeId) {
                @let cancelled = bundleTagCancelled(row.groupId, row.roleId, roleScope.scopeId);
                @let restricted = bundleTagRestricted(row.groupId, row.roleId, roleScope.scopeId);
                <p-tag
                  tooltipPosition="top"
                  [severity]="cancelled ? 'secondary' : restricted ? 'warn' : 'info'"
                  [value]="roleScope.scopeName || scopeLabel(roleScope.scopeId)"
                  [class.scope-cancelled]="cancelled"
                  [class.scope-restricted]="restricted"
                  [pTooltip]="bundleTagTooltip(row.groupId, row.roleId, roleScope.scopeId)"
                ></p-tag>
              }
            </div>
          }
        </div>
      }
    }

    <h3 class="section-heading">Overrides on this user</h3>
    @if (grantedToResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (grantedToResource.error()) {
      <p-message severity="error" text="Failed to load the overrides written about this user."></p-message>
    } @else if ((grantedToResource.value() ?? []).length === 0) {
      <p class="muted">Nothing has been granted to or withheld from this user.</p>
    } @else {
      <p-table
        data-testid="overrides-about-user"
        [value]="grantedToResource.value() ?? []"
        [tableStyle]="{ 'min-width': '56rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Scope</th>
            <th>Effect</th>
            <th>Over</th>
            <th>Written By</th>
            <th>Written</th>
            <th>Status</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-permission>
          <tr>
            <td
              class="monospace"
              tooltipPosition="top"
              [class.scope-cancelled]="cancellingDenyForOverride(permission)"
              [pTooltip]="cancellingDenyForOverrideTooltip(permission)"
            >
              {{ scopeLabel(permission.scopeId) }}
            </td>
            <td>
              <p-tag
                [value]="getPermissionEffect(permission)"
                [severity]="
                  cancellingDenyForOverride(permission) ? 'secondary' : getPermissionEffectSeverity(permission)
                "
              ></p-tag>
            </td>
            <td>{{ objectLabel(permission) }}</td>
            <td>
              <a [routerLink]="['/access-management/users', permission.grantorUserId]">
                {{ userLabel(permission.grantorUserId) }}
              </a>
            </td>
            <td>{{ formatDate(permission.createdOn) }}</td>
            <td>
              <p-tag
                [value]="getPermissionStatus(permission)"
                [severity]="getPermissionStatusSeverity(permission)"
              ></p-tag>
            </td>
          </tr>
        </ng-template>
      </p-table>
    }

    <h3 class="section-heading">Overrides written by this user</h3>
    @if (grantedByResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (grantedByResource.error()) {
      <p-message severity="error" text="Failed to load the overrides written by this user."></p-message>
    } @else if ((grantedByResource.value() ?? []).length === 0) {
      <p class="muted">This user has not granted or withheld anything.</p>
    } @else {
      <p-table
        data-testid="overrides-by-user"
        [value]="grantedByResource.value() ?? []"
        [tableStyle]="{ 'min-width': '56rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Scope</th>
            <th>Effect</th>
            <th>Over</th>
            <th>Applies To</th>
            <th>Written</th>
            <th>Status</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-permission>
          <tr>
            <td class="monospace">{{ scopeLabel(permission.scopeId) }}</td>
            <td>
              <p-tag
                [value]="getPermissionEffect(permission)"
                [severity]="getPermissionEffectSeverity(permission)"
              ></p-tag>
            </td>
            <td>{{ objectLabel(permission) }}</td>
            <td>
              <a [routerLink]="['/access-management/users', permission.granteeUserId]">
                {{ userLabel(permission.granteeUserId) }}
              </a>
            </td>
            <td>{{ formatDate(permission.createdOn) }}</td>
            <td>
              <p-tag
                [value]="getPermissionStatus(permission)"
                [severity]="getPermissionStatusSeverity(permission)"
              ></p-tag>
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  </p-card>

  <p-card header="Guardians &amp; Dependents">
    <p class="card-subtitle">
      Guardian assignments from this user's household membership. Names link to the related user.
    </p>
    @if (householdResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (householdResource.error()) {
      <p-message severity="error" text="Failed to load household membership."></p-message>
    } @else if (!householdSelf()) {
      <p class="muted">This user is not a member of a household group.</p>
    } @else {
      <div class="detail-grid">
        <div class="detail-row">
          <span class="detail-label">Guardians</span>
          <span class="detail-value">
            @if (guardians().length === 0) {
              <span class="muted">—</span>
            } @else {
              <ul class="related-user-list">
                @for (guardian of guardians(); track guardian.youtopianId) {
                  <li>
                    <a [routerLink]="['/access-management/users', guardian.youtopianId]">
                      {{ guardian.name || guardian.youtopianId }}
                    </a>
                  </li>
                }
              </ul>
            }
          </span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Dependents</span>
          <span class="detail-value">
            @if (dependents().length === 0) {
              <span class="muted">—</span>
            } @else {
              <ul class="related-user-list">
                @for (dependent of dependents(); track dependent.youtopianId) {
                  <li>
                    <a [routerLink]="['/access-management/users', dependent.youtopianId]">
                      {{ dependent.name || dependent.youtopianId }}
                    </a>
                  </li>
                }
              </ul>
            }
          </span>
        </div>
      </div>
    }
  </p-card>

  <p-card header="Invitation-scoped consents">
    <p class="card-subtitle">
      Consent timestamps captured on invitations sent by this user. Empty cells mean the consent was not required or not
      yet captured.
    </p>
    @if (invitationsResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (invitationsResource.error()) {
      <p-message severity="error" text="Failed to load invitations."></p-message>
    } @else if ((invitationsResource.value() ?? []).length === 0) {
      <p class="muted">This user has not sent any invitations.</p>
    } @else {
      <p-table [value]="invitationsResource.value() ?? []" [scrollable]="true" [tableStyle]="{ 'min-width': '60rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Invitee</th>
            <th>Status</th>
            <th>Sent</th>
            <th>Guardian</th>
            <th>ToS</th>
            <th>Privacy</th>
            <th>Dependent Resp.</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-inv>
          <tr>
            <td>{{ inv.inviteeEmail }}</td>
            <td><p-tag [value]="inv.status" [severity]="getInvitationStatusSeverity(inv.status)"></p-tag></td>
            <td>{{ formatDate(inv.createdOn) }}</td>
            <td>{{ formatDate(inv.guardianConsentAcceptedOn) }}</td>
            <td>{{ formatDate(inv.tosConsentAcceptedOn) }}</td>
            <td>{{ formatDate(inv.privacyConsentAcceptedOn) }}</td>
            <td>{{ formatDate(inv.dependentResponsibilityConsentAcceptedOn) }}</td>
          </tr>
        </ng-template>
      </p-table>
    }
  </p-card>

  <p-card header="Entitlements">
    @if (entitlementsResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (entitlementsResource.error()) {
      <p-message severity="error" text="Failed to load entitlements from AccessManagementEngine."></p-message>
    } @else if ((entitlementsResource.value()?.allocations ?? []).length === 0) {
      <p class="muted">This user has no feature allocations.</p>
    } @else {
      <p-table
        [value]="entitlementsResource.value()?.allocations ?? []"
        [scrollable]="true"
        [tableStyle]="{ 'min-width': '60rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Feature</th>
            <th>Owner</th>
            <th>Cap</th>
            <th>Available</th>
            <th>Period</th>
            <th>State</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-allocation>
          <tr>
            <td>{{ allocation.featureName || allocation.featureCode }}</td>
            <td>{{ allocation.isGroupLevel ? 'Group' : 'Membership' }}</td>
            <td>{{ allocation.cap ?? '—' }}</td>
            <td>{{ allocation.availableCount ?? '—' }}</td>
            <td>{{ allocation.periodIndex ?? '—' }}</td>
            <td>
              <p-tag
                [value]="getAllocationStateLabel(allocation)"
                [severity]="getAllocationStateSeverity(allocation)"
              ></p-tag>
            </td>
            <td class="actions-cell">
              <a [routerLink]="['/entitlements/allocations', allocation.featureAllocationId]">Manage</a>
              @if (allocation.isDeactivated) {
                <p-button
                  label="Reactivate"
                  size="small"
                  severity="success"
                  [text]="true"
                  [loading]="togglingAllocationId() === allocation.featureAllocationId"
                  (onClick)="onReactivateAllocation(allocation)"
                ></p-button>
              } @else {
                <p-button
                  label="Deactivate"
                  size="small"
                  severity="danger"
                  [text]="true"
                  [loading]="togglingAllocationId() === allocation.featureAllocationId"
                  (onClick)="onDeactivateAllocation(allocation)"
                ></p-button>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    }
  </p-card>

  @if (membershipExpirationEnabled && membershipIds().length > 0) {
    <p-card header="Membership expiration">
      @for (membershipId of membershipIds(); track membershipId; let count = $count) {
        @if (count > 1) {
          <h3 class="section-heading">Membership {{ membershipId }}</h3>
        }
        <app-membership-expiration-panel
          [membershipId]="membershipId"
          [attr.data-testid]="'membership-expiration-panel-' + membershipId"
          (adjusted)="entitlementsResource.reload()"
        />
      }
    </p-card>
  }

  <p-card header="Daily results">
    @if (userId(); as youtopianId) {
      <app-daily-results-panel [youtopianId]="youtopianId"></app-daily-results-panel>
    }
  </p-card>

  <app-delete-entity-dialog
    entityKind="user"
    [entityName]="userResource.value()?.name ?? ''"
    [submitting]="deleting()"
    [(visible)]="showDeleteDialog"
    (confirm)="onConfirmDelete($event)"
  ></app-delete-entity-dialog>
</div>
